import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { AuthGuard } from '@/components/guards/AuthGuard';
import { GuestGuard } from '@/components/guards/GuestGuard';
import { AppLayout } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CommandPalette } from '@/components/ui/CommandPalette';
import { ToastContainer } from '@/components/ui/Toast';
import { CookieConsent } from '@/components/ui/CookieConsent';

const LoginPage = lazy(() => import('@/views/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('@/views/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('@/views/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/views/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const DashboardPage = lazy(() => import('@/views/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const WorkspacePage = lazy(() => import('@/views/WorkspacePage').then((m) => ({ default: m.WorkspacePage })));
const ProjectBoardPage = lazy(() => import('@/views/ProjectBoardPage').then((m) => ({ default: m.ProjectBoardPage })));
const ProjectListPage = lazy(() => import('@/views/ProjectListPage').then((m) => ({ default: m.ProjectListPage })));
const CalendarPage = lazy(() => import('@/views/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const MyTasksPage = lazy(() => import('@/views/MyTasksPage').then((m) => ({ default: m.MyTasksPage })));
const MyDayPage = lazy(() => import('@/views/MyDayPage').then((m) => ({ default: m.MyDayPage })));
const TimeTrackingPage = lazy(() => import('@/views/TimeTrackingPage').then((m) => ({ default: m.TimeTrackingPage })));
const ActivityFeedPage = lazy(() => import('@/views/ActivityFeedPage').then((m) => ({ default: m.ActivityFeedPage })));
const ArchivePage = lazy(() => import('@/views/ArchivePage').then((m) => ({ default: m.ArchivePage })));
const AdminPage = lazy(() => import('@/views/AdminPage').then((m) => ({ default: m.AdminPage })));
const BookmarksPage = lazy(() => import('@/views/BookmarksPage').then((m) => ({ default: m.BookmarksPage })));
const AcceptInvitationPage = lazy(() => import('@/views/AcceptInvitationPage').then((m) => ({ default: m.AcceptInvitationPage })));
const PublicBoardPage = lazy(() => import('@/views/PublicBoardPage').then((m) => ({ default: m.PublicBoardPage })));
const AbsencesPage = lazy(() => import('@/views/AbsencesPage').then((m) => ({ default: m.AbsencesPage })));
const NotificationsPage = lazy(() => import('@/views/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const UserSettingsPage = lazy(() => import('@/views/UserSettingsPage').then((m) => ({ default: m.UserSettingsPage })));
const WorkspaceSettingsPage = lazy(() => import('@/views/WorkspaceSettingsPage').then((m) => ({ default: m.WorkspaceSettingsPage })));
const TimelinePage = lazy(() => import('@/views/TimelinePage').then((m) => ({ default: m.TimelinePage })));
const GFViewPage = lazy(() => import('@/views/GFViewPage').then((m) => ({ default: m.GFViewPage })));
const NotFoundPage = lazy(() => import('@/views/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));

function App() {
  const fetchMe = useAuthStore((s) => s.fetchMe);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  return (
    <ErrorBoundary>
    <Suspense fallback={<Spinner className="min-h-screen" />}>
      <CommandPalette />
      <ToastContainer />
      <CookieConsent />
      <Routes>
        {/* Guest routes */}
        <Route path="/login" element={<GuestGuard><LoginPage /></GuestGuard>} />
        <Route path="/register" element={<GuestGuard><RegisterPage /></GuestGuard>} />
        <Route path="/forgot-password" element={<GuestGuard><ForgotPasswordPage /></GuestGuard>} />
        <Route path="/reset-password" element={<GuestGuard><ResetPasswordPage /></GuestGuard>} />

        {/* Authenticated routes */}
        <Route element={<AuthGuard><AppLayout /></AuthGuard>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/my-day" element={<MyDayPage />} />
          <Route path="/my-tasks" element={<MyTasksPage />} />
          <Route path="/bookmarks" element={<BookmarksPage />} />
          <Route path="/time-tracking" element={<TimeTrackingPage />} />
          <Route path="/w/:workspaceId" element={<WorkspacePage />} />
          <Route path="/w/:workspaceId/p/:projectId" element={<ProjectBoardPage />} />
          <Route path="/w/:workspaceId/p/:projectId/list" element={<ProjectListPage />} />
          <Route path="/w/:workspaceId/p/:projectId/timeline" element={<TimelinePage />} />
          <Route path="/w/:workspaceId/p/:projectId/calendar" element={<CalendarPage />} />
          <Route path="/w/:workspaceId/activity" element={<ActivityFeedPage />} />
          <Route path="/w/:workspaceId/absences" element={<AbsencesPage />} />
          <Route path="/w/:workspaceId/archive" element={<ArchivePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/gf-view" element={<GFViewPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<UserSettingsPage />} />
          <Route path="/w/:workspaceId/settings" element={<WorkspaceSettingsPage />} />
        </Route>

        {/* Public routes */}
        <Route path="/invite" element={<AcceptInvitationPage />} />
        <Route path="/public/:token" element={<PublicBoardPage />} />

        {/* Fallback */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

export default App;
